"use client";

import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatTimeWithPreference } from "@/lib/format";
import { useTimeFormat } from "@/lib/time-format-context";
import { format, parseISO } from "date-fns";
import { Plus, Link as LinkIcon, X } from "lucide-react";
import { BlockFormSheet } from "@/components/blocks/block-form-sheet";

interface LinkShiftDialogProps {
  shiftId: Id<"shifts"> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LinkShiftDialog({
  shiftId,
  open,
  onOpenChange,
}: LinkShiftDialogProps) {
  const timeFormat = useTimeFormat();
  const [showCreate, setShowCreate] = useState(false);

  const shift = useQuery(api.shifts.getShift, shiftId ? { id: shiftId } : "skip");
  const unlinkedBlocks = useQuery(api.blocks.getUnlinkedBlocks);
  const linkShift = useMutation(api.shifts.linkShiftToBlock);

  const handleLink = async (blockId: Id<"blocks">) => {
    if (!shiftId) return;
    await linkShift({ shiftId, blockId });
    onOpenChange(false);
  };

  if (!shift) return null;

  return (
    <>
      <Dialog open={open && !showCreate} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Link to Block</DialogTitle>
            <DialogDescription>
              {shift.totalMiles.toFixed(1)} miles tracked. Link to a block for
              complete data.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-4">
            {unlinkedBlocks && unlinkedBlocks.length > 0 ? (
              <>
                <p className="text-sm font-medium">Available Blocks</p>
                <div className="space-y-2">
                  {unlinkedBlocks.map((block) => {
                    const total = block.basePay + block.tip + block.noRoutePayout;
                    return (
                      <button
                        key={block._id}
                        className="w-full p-3 border rounded-lg text-left hover:bg-accent transition-colors"
                        onClick={() => handleLink(block._id)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">
                              {formatTimeWithPreference(block.shiftStart, timeFormat)} – {formatTimeWithPreference(block.shiftEnd, timeFormat)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {format(parseISO(block.date), "MMM d")}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">
                              {formatCurrency(total)}
                            </p>
                            <LinkIcon className="h-4 w-4 text-muted-foreground ml-auto" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No unlinked blocks available.
              </p>
            )}

            <div className="flex flex-col gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreate(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Block
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                <X className="h-4 w-4 mr-2" />
                Skip
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BlockFormSheet
        mode="create"
        open={showCreate}
        onOpenChange={(isOpen) => {
          setShowCreate(isOpen);
          if (!isOpen) {
            // Re-open link dialog after sheet closes
            onOpenChange(true);
          }
        }}
      />
    </>
  );
}
